import React from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { Text } from 'components';
import { useI18n } from 'components/I18n';
import { ClickTracker } from 'components/Tracking';
import Search from './Search';
import UserMenu from './UserMenu';
import { MenuLink, StyledPersistentNavigation, SignUpButton, SignUpButtonWrapper } from './Styles';

function PersistentNavigation({ shouldShowSignUp, persistentNavItems, signUpBtnRef }) {
  const i18n = useI18n();

  const {
    isLoggedIn,
    isAnonymousUser,
    location,
    loginEnabled,
    hideItemsForUnentitled,
    horizontalMenuHidden,
  } = useSelector(state => ({
    isLoggedIn: state.auth.isLoggedIn,
    isAnonymousUser: state.auth.isAnonymousUser,
    location: state.router.location,
    loginEnabled: state.settings.loginEnabled,
    hideItemsForUnentitled: state.auth.isLoggedIn
      && state.settings.features.payment?.onboarding?.mandatory
      && !state.packs.entitled?.length,
    horizontalMenuHidden: state.ui.horizontalMenuHidden,
  }), shallowEqual);

  const searchItem = persistentNavItems?.find(item => item.type === 'search');
  const shouldShowSearch = !hideItemsForUnentitled && !!searchItem;
  const shouldShowUserMenu = isLoggedIn && !isAnonymousUser;
  const shouldShowLogin = loginEnabled && (!isLoggedIn || isAnonymousUser);

  if (!shouldShowSearch && !shouldShowUserMenu && !shouldShowLogin && !shouldShowSignUp) {
    return null;
  }

  return (
    <StyledPersistentNavigation aria-label={i18n.formatText('aria.navigation.userLabel')}>
      {shouldShowSearch && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="Search"
        >
          <Search asLink={horizontalMenuHidden ?? false} />
        </ClickTracker>
      )}
      {shouldShowUserMenu && <UserMenu />}

      {shouldShowLogin && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="Login"
        >
          <MenuLink
            to={{ name: 'log-in' }}
            className="e2e-log-in"
            data-content={i18n.formatText('logIn')}
            active={location.name === 'log-in'}
          >
            <Text id="logIn" />
          </MenuLink>
        </ClickTracker>
      )}
      {shouldShowSignUp && (
        <ClickTracker
          component="Menu"
          clickType="navigation"
          eventName="SignUp"
        >
          <SignUpButtonWrapper
            innerRef={signUpBtnRef}
            hidden={!!horizontalMenuHidden}
            aria-hidden={!!horizontalMenuHidden}
          >
            <SignUpButton
              to={{ name: 'create-account' }}
              data-content={i18n.formatText('menu.signUp')}
              active={location.name === 'create-account'}
              tabIndex={horizontalMenuHidden ? -1 : 0}
            >
              <Text id="menu.signUp" />
            </SignUpButton>
          </SignUpButtonWrapper>
        </ClickTracker>
      )}
    </StyledPersistentNavigation>
  );
}

PersistentNavigation.propTypes = {
  shouldShowSignUp: PropTypes.bool,
  persistentNavItems: PropTypes.array,
  hideItemsForUnentitled: PropTypes.bool,
  signUpBtnRef: PropTypes.object,
};

export default React.memo(PersistentNavigation);
